import { useMemo } from 'react';
import { Box, Paper, Stack, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import type { Project, TimeEntry, User } from '@altyor-kronos/shared-types';
import {
  entriesByProject,
  entriesByUserAndProject,
  hoursFromSeconds,
  latestEntryByUser,
  rankedByValue,
} from '../dashboardAggregations';
import { formatDurationSeconds } from '../timeEntries';
import { colors } from '../../../core/theme/theme';

/**
 * Team member list for the rd_manager dashboard — Phase 7.x.
 *
 * Layout per row (matching the screenshot the user shared):
 *   [avatar] [name]  [latest activity]  [weekly total]  [stacked bar]
 *
 * The stacked bar is the "jauge de saisie": empty bar + "—" for the
 * total = no time logged this week, i.e. the compliance signal
 * that Phase 13 `dailyTimeReminder` will act on. Active users sort
 * to the top, inactive to the bottom, secondary sort by total desc.
 */

const AVATAR_COLORS = [
  '#7C3AED', // violet
  '#DC2626', // red-600
  '#EA580C', // orange-600
  '#CA8A04', // amber-600
  '#16A34A', // green-600
  '#0891B2', // cyan-600
  '#2563EB', // blue-600
  '#DB2777', // pink-600
];

export interface TeamMemberListProps {
  /** Entries for the week being visualised (already time-filtered). */
  weekEntries: readonly TimeEntry[];
  /** All users the rd_manager can see — one row per user. */
  users: ReadonlyArray<{ id: string; data: User }>;
  projectsById: ReadonlyMap<string, Project>;
  /**
   * Stable project → colour map. The parent passes the same one the
   * aggregation panel uses so the team-list bars share colours with
   * the donut and ranked project list.
   */
  projectColorById: ReadonlyMap<string, string>;
}

export default function TeamMemberList(props: TeamMemberListProps) {
  const { weekEntries, users, projectsById, projectColorById } = props;
  const { t, i18n } = useTranslation();

  const now = useMemo(() => Date.now(), []);
  const byUserProject = useMemo(() => entriesByUserAndProject(weekEntries), [weekEntries]);
  const latestByUser = useMemo(() => latestEntryByUser(weekEntries), [weekEntries]);

  // Shared scale: normalise every user's bar against the busiest
  // person's total. This makes "who's doing more than me" instantly
  // readable while keeping empty bars truly empty (no artificial
  // floor).
  const maxUserTotal = useMemo(() => {
    let max = 0;
    for (const inner of byUserProject.values()) {
      let total = 0;
      for (const v of inner.values()) total += v;
      if (total > max) max = total;
    }
    return max;
  }, [byUserProject]);

  // Only show members with activity this week, ranked by volume desc.
  // Teammates with no logged time are filtered out entirely (the empty
  // bar / "—" rows were noise) — when nobody has logged time the
  // empty-state message below takes over.
  const rows = useMemo(() => {
    const buildRow = (u: { id: string; data: User }) => {
      const inner = byUserProject.get(u.id);
      const segments = inner ? rankedByValue(inner) : [];
      const total = segments.reduce((s, r) => s + r.value, 0);
      return {
        uid: u.id,
        user: u.data,
        segments,
        totalSeconds: total,
        latest: latestByUser.get(u.id),
      };
    };
    return users
      .map(buildRow)
      .filter((r) => r.totalSeconds > 0)
      .sort((a, b) => b.totalSeconds - a.totalSeconds);
  }, [users, byUserProject, latestByUser]);

  return (
    <Paper
      variant="outlined"
      sx={{ p: 0, borderColor: colors.lightGray, borderRadius: 0 }}
      data-testid="team-member-list"
    >
      <HeaderRow t={t} />
      {rows.length === 0 ? (
        <Box sx={{ p: 3, textAlign: 'center' }}>
          <Typography variant="body2" color="text.secondary">
            {t('kronos.dashboard.noData')}
          </Typography>
        </Box>
      ) : (
        rows.map((r) => (
          <TeamRow
            key={r.uid}
            row={r}
            projectsById={projectsById}
            projectColorById={projectColorById}
            maxUserTotal={maxUserTotal}
            now={now}
            t={t}
            locale={i18n.language}
          />
        ))
      )}
    </Paper>
  );
}

// ─── Row components ────────────────────────────────────────────────

function HeaderRow({ t }: { t: (k: string) => string }) {
  return (
    <Box
      sx={{
        display: 'grid',
        gridTemplateColumns: { xs: '1fr', md: '220px 1fr 120px 2fr' },
        alignItems: 'center',
        gap: 2,
        p: 2,
        bgcolor: colors.lightGray,
        borderBottom: `2px solid ${colors.bodyText}`,
      }}
    >
      <Typography variant="caption" sx={{ fontWeight: 700 }}>
        {t('kronos.dashboard.teamMember')}
      </Typography>
      <Typography variant="caption" sx={{ fontWeight: 700 }}>
        {t('kronos.dashboard.latestActivity')}
      </Typography>
      <Typography variant="caption" sx={{ fontWeight: 700, textAlign: 'right' }}>
        {t('kronos.dashboard.totalWeek')}
      </Typography>
      <Typography variant="caption" sx={{ fontWeight: 700 }}>
        {t('kronos.dashboard.composition')}
      </Typography>
    </Box>
  );
}

interface Row {
  uid: string;
  user: User;
  segments: Array<{ key: string; value: number; percent: number }>;
  totalSeconds: number;
  latest: TimeEntry | undefined;
}

function TeamRow({
  row,
  projectsById,
  projectColorById,
  maxUserTotal,
  now,
  t,
  locale,
}: {
  row: Row;
  projectsById: ReadonlyMap<string, Project>;
  projectColorById: ReadonlyMap<string, string>;
  maxUserTotal: number;
  now: number;
  t: (k: string) => string;
  locale: string;
}) {
  const barWidthPct =
    maxUserTotal > 0 ? (row.totalSeconds / maxUserTotal) * 100 : 0;

  const latest = row.latest;
  const latestProject = latest ? projectsById.get(latest.projectId) : undefined;

  return (
    <Box
      sx={{
        display: 'grid',
        gridTemplateColumns: { xs: '1fr', md: '220px 1fr 120px 2fr' },
        alignItems: 'center',
        gap: 2,
        p: 2,
        borderBottom: `1px solid ${colors.lightGray}`,
        '&:last-of-type': { borderBottom: 'none' },
      }}
      data-testid="team-row"
    >
      {/* ── Avatar + name ──────────────────────────────────── */}
      <Stack direction="row" spacing={1.5} sx={{ alignItems: 'center' }}>
        <Avatar displayName={row.user.displayName} photoURL={row.user.photoURL} />
        <Typography variant="body2" sx={{ fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {row.user.displayName}
        </Typography>
      </Stack>

      {/* ── Latest activity ────────────────────────────────── */}
      <Box sx={{ overflow: 'hidden' }}>
        {latest ? (
          <>
            <Typography variant="body2" sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {latestProject
                ? `${latestProject.code} — ${latest.description || t('kronos.dashboard.noDescription')}`
                : latest.description || t('kronos.dashboard.noDescription')}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {formatRelativeTime(latest.startTime.toMillis(), now, locale)}
            </Typography>
          </>
        ) : (
          <Typography variant="caption" color="text.secondary">
            {t('kronos.dashboard.noActivityThisWeek')}
          </Typography>
        )}
      </Box>

      {/* ── Total this week ────────────────────────────────── */}
      <Typography variant="body2" sx={{ textAlign: 'right', fontWeight: 600, fontVariantNumeric: 'tabular-nums' }}>
        {row.totalSeconds > 0 ? formatDurationSeconds(row.totalSeconds) : '—'}
      </Typography>

      {/* ── Stacked composition bar ────────────────────────── */}
      <Box
        sx={{
          position: 'relative',
          height: 12,
          bgcolor: colors.lightGray,
          overflow: 'hidden',
        }}
        data-testid="team-row-bar"
      >
        {row.totalSeconds > 0 && (
          <Box
            sx={{
              display: 'flex',
              height: '100%',
              width: `${barWidthPct}%`,
            }}
          >
            {row.segments.map((seg) => (
              <Box
                key={seg.key}
                sx={{
                  flex: seg.value,
                  bgcolor: projectColorById.get(seg.key) ?? colors.textMuted,
                }}
                title={`${projectsById.get(seg.key)?.code ?? seg.key}: ${hoursFromSeconds(seg.value).toFixed(1)}h`}
              />
            ))}
          </Box>
        )}
      </Box>
    </Box>
  );
}

function Avatar({ displayName, photoURL }: { displayName: string; photoURL?: string }) {
  if (photoURL) {
    return (
      <Box
        component="img"
        src={photoURL}
        alt={displayName}
        sx={{ width: 32, height: 32, objectFit: 'cover' }}
      />
    );
  }
  const initials = getInitials(displayName);
  // Stable per-user colour derived from the display name hash so
  // the same person always gets the same avatar tint across page
  // reloads and sessions.
  const color = AVATAR_COLORS[hashString(displayName) % AVATAR_COLORS.length];
  return (
    <Box
      sx={{
        width: 32,
        height: 32,
        bgcolor: color,
        color: colors.white,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        fontWeight: 700,
        fontSize: 12,
        // Square per design guide — no border radius.
      }}
    >
      {initials}
    </Box>
  );
}

// ─── Pure helpers ──────────────────────────────────────────────────

function getInitials(name: string): string {
  if (!name) return '??';
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return '??';
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
}

function hashString(s: string): number {
  if (!s) return 0;
  // Tiny FNV-1a-ish hash — good enough for colour bucketing, fast,
  // deterministic across sessions.
  let h = 2166136261 >>> 0;
  for (let i = 0; i < s.length; i++) {
    h ^= s.charCodeAt(i);
    h = Math.imul(h, 16777619) >>> 0;
  }
  return h;
}

function formatRelativeTime(whenMs: number, nowMs: number, locale: string): string {
  const diffSec = Math.max(0, Math.round((nowMs - whenMs) / 1000));
  const rtf = new Intl.RelativeTimeFormat(locale, { numeric: 'auto' });
  if (diffSec < 60) return rtf.format(-diffSec, 'second');
  const diffMin = Math.round(diffSec / 60);
  if (diffMin < 60) return rtf.format(-diffMin, 'minute');
  const diffHour = Math.round(diffMin / 60);
  if (diffHour < 24) return rtf.format(-diffHour, 'hour');
  const diffDay = Math.round(diffHour / 24);
  if (diffDay < 30) return rtf.format(-diffDay, 'day');
  const diffMonth = Math.round(diffDay / 30);
  return rtf.format(-diffMonth, 'month');
}

// Re-export aggregator used in the row so callers can keep imports
// minimal.
export { entriesByProject };
